import React, { useEffect, useState } from 'react';
import { Truck, Save, Loader2, Info, ExternalLink } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminShippingCosts = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [tiers, setTiers] = useState([]);
  const [drafts, setDrafts] = useState({}); // { [tierId]: shipping_cost_string }
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);

  const loadTiers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/admin/discount-tiers`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setTiers(list);
      const map = {};
      list.forEach(t => { map[t.id] = String(t.shipping_cost ?? 0); });
      setDrafts(map);
    } catch (e) {
      toast({ title: 'Errore', description: 'Impossibile caricare le fasce', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminToken) loadTiers(); }, [adminToken]); // eslint-disable-line

  const saveOne = async (tier) => {
    const raw = drafts[tier.id];
    const value = parseFloat(String(raw).replace(',', '.'));
    if (isNaN(value) || value < 0) {
      toast({ title: 'Valore non valido', description: 'Inserisci un numero >= 0', variant: 'destructive' });
      return;
    }
    setSavingId(tier.id);
    try {
      const res = await fetch(`${backendUrl}/api/admin/discount-tiers/${tier.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ shipping_cost: value })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Salvataggio fallito');
      }
      toast({ title: 'Salvato', description: `Costo spedizione aggiornato per "${tier.label}"` });
      await loadTiers();
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const saveAll = async () => {
    setSavingAll(true);
    try {
      for (const tier of tiers) {
        const raw = drafts[tier.id];
        const value = parseFloat(String(raw).replace(',', '.'));
        if (isNaN(value) || value < 0) continue;
        if (Number(tier.shipping_cost || 0) === value) continue;
        await fetch(`${backendUrl}/api/admin/discount-tiers/${tier.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ shipping_cost: value })
        });
      }
      toast({ title: 'Salvato', description: 'Tutte le modifiche sono state salvate' });
      await loadTiers();
    } catch (e) {
      toast({ title: 'Errore', description: 'Salvataggio parziale non riuscito', variant: 'destructive' });
    } finally {
      setSavingAll(false);
    }
  };

  const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-shipping-costs">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Truck className="w-8 h-8 text-orange-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Costi Spedizioni</h1>
              <p className="text-gray-600 mt-1">
                Definisci il costo di spedizione per ogni fascia di quantità (bottiglie totali) del listino.
              </p>
            </div>
          </div>
          <button
            onClick={saveAll}
            disabled={savingAll || tiers.length === 0}
            data-testid="btn-save-all-shipping"
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
          >
            {savingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva tutte
          </button>
        </div>

        {/* Info box */}
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded flex items-start gap-3">
          <Info className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
          <div className="text-sm text-orange-800">
            <p>
              <strong>Come funziona:</strong> il costo di spedizione è associato a ciascuna fascia di
              quantità bottiglie (le stesse gestite da <a href="/admin/discount-tiers" className="underline inline-flex items-center gap-0.5">Sconti Quantità <ExternalLink className="w-3 h-3" /></a>).
              Il costo viene applicato automaticamente al preventivo in base al totale bottiglie richieste ed è mostrato anche in fase di creazione preventivo (Admin/Agente).
            </p>
            <p className="mt-2">
              I costi sono espressi in <strong>Euro IVA esclusa</strong>. L&apos;IVA 22% verrà applicata automaticamente sul totale del preventivo.
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fascia</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Range Bottiglie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sconto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Spedizione (€)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Azione</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tiers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <Truck className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>Nessuna fascia configurata.</p>
                      <p className="text-sm mt-1">
                        Prima crea le fasce in{' '}
                        <a href="/admin/discount-tiers" className="text-purple-600 underline">Sconti Quantità</a>.
                      </p>
                    </td>
                  </tr>
                ) : (
                  tiers.map((tier) => {
                    const currentValue = Number(tier.shipping_cost || 0);
                    const draftValue = drafts[tier.id];
                    const numericDraft = parseFloat(String(draftValue).replace(',', '.'));
                    const hasChanges = !isNaN(numericDraft) && numericDraft !== currentValue;
                    return (
                      <tr key={tier.id} className={hasChanges ? 'bg-orange-50/40' : ''} data-testid={`shipping-row-${tier.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{tier.label}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {tier.min_quantity} – {tier.max_quantity ?? '∞'} bot.
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-green-600">
                            {(tier.discount_percentage * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-sm">€</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={draftValue ?? ''}
                              onChange={(e) => setDrafts(prev => ({ ...prev, [tier.id]: e.target.value }))}
                              data-testid={`shipping-input-${tier.id}`}
                              className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 tabular-nums"
                            />
                            {hasChanges && (
                              <span className="text-xs text-orange-600 font-medium">non salvato</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">Attuale: {eur(currentValue)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${tier.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                            {tier.is_active ? 'Attivo' : 'Disattivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => saveOne(tier)}
                            disabled={savingId === tier.id || !hasChanges}
                            data-testid={`btn-save-shipping-${tier.id}`}
                            className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white text-xs rounded-md hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {savingId === tier.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            Salva
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer note */}
        <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
          Suggerimento: se una fascia di quantità non è ancora presente, creala prima da{' '}
          <a href="/admin/discount-tiers" className="text-purple-600 underline">Sconti Quantità</a>,
          poi assegna qui il relativo costo di spedizione.
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminShippingCosts;
